/**
 * Transmute — migrate stock from AML-equivalent alternate SKUs to a chosen target SKU inside Megaventory
 * via verified adjustment minus / plus pairs. Matches the Manufacturing Operations Appsmith flow and adds
 * unit conversions that preserve extended value (`qty × unit cost`).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";

import { apiFetch, ApiError } from "@/lib/api";
import { AssemblySkuCombobox } from "@/components/mrp/AssemblySkuCombobox";
import { MrpSkuDetailLink } from "@/components/MrpSkuDetailLink";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { OperationalLocationNativeSelectOptions } from "@/components/mrp/TransferEndpointLocationSelect";
import { Input, Label } from "@/components/ui/Input";
import { CardTitleWithHelp, HoverHintWithGlyph, PageTitleWithHelp } from "@/components/ui/title-help";
import { cn } from "@/lib/utils";
import {
  useTransferEndpointLocations,
} from "@/lib/transferEndpointLocations";
import type { MegaventoryTransmuteExecutedPair } from "@integrator-src/mrp/mrp-transmute-types";
import { conversionFactorTargetPerSource } from "@integrator-src/mrp/mrp-transmute-units";

/** Card and field help strings (plain text only; surfaced via HoverHintWithGlyph hover or focus-in). */
const HELP_TRANSMUTE_PAGE =
  "Removes approved material list manufacturing alternates with Megaventory adjustment minus documents and adds the chosen target stock keeping unit with adjustment plus at one Megaventory-linked hub warehouse you pick. Quantity and unit cost scale when units of measure differ so extended value stays the same. Manual transmute skips alternate validation for one from or to pairing at a single warehouse.";

const HELP_AML_CARD =
  "Pick the stocked target customer part number and the hub warehouse for this operation. The inventory snapshot refreshes automatically after target and warehouse settle (reload snapshot is still available). Load pulls the target customer part number plus approved interchange manufacturing codes only at that warehouse. Choose one alternate row with on-hand quantity to withdraw while adjusting matching value into the highlighted target row. Warehouse stock favors physical shelf quantity when Megaventory returns it and otherwise Megaventory forecast on-hand. The optional multiplier is how many target units you receive for each one source unit; leave it blank to use the value suggested from the two unit-of-measure labels when the hub can derive it.";

const HELP_MANUAL_CARD =
  "Runs one Megaventory adjustment minus and matching adjustment plus for typed from and to product codes when the approved material list does not sanction the pairing. Use the optional multiplier field when unit labels do not line up with what you need.";

const HELP_SESSION_CARD =
  "Shows transmute successes and failures from this browser tab this session only. Reloading clears the log.";

const HELP_TARGET_SKU =
  "Customer stocked part number; the product that gains inventory after the plus adjustment completes.";

const HELP_AML_LOCATION =
  "Loads inventory snapshots and posts adjustment minus and adjustment plus through this Megaventory-linked hub warehouse only.";

const HELP_OPTIONAL_REMARK =
  "Optional free text appended to Megaventory adjustment document comments for this execution.";

const HELP_MANUAL_FROM =
  "Source product stock keeping unit Megaventory removes from warehouse stock via adjustment minus.";

const HELP_MANUAL_TO =
  "Destination product stock keeping unit Megaventory adds via adjustment plus; usually the stocked customer part number receiving material.";

const HELP_MANUAL_LOCATION =
  "Hub warehouse where Megaventory posts this minus and plus adjustment pair.";

const HELP_MANUAL_QTY =
  "Quantity withdrawn from the from-product side in Megaventory source product units.";

const HELP_MANUAL_FACTOR =
  "Multiplier: how many target units you receive for each one source unit (for example 453.59237 when one pound becomes nominal grams). Leave blank when both products use the same unit or when the hub can infer mass or count conversion from the unit labels.";

const HELP_TH_SOURCE_ROW =
  "Pick exactly one alternate line that has on-hand stock as the source part number to withdraw at this warehouse. Lines with zero stock cannot be a source. The row that matches your target customer part number is shown only as the receiving line — it is not a source (transmuting a part into itself is not allowed).";
const HELP_TH_SKU =
  "Megaventory ProductSKU identifying the row.";
const HELP_TH_LOCATION = "Megaventory-linked hub warehouse shown for this snapshot row.";
const HELP_TH_STOCK = "Megaventory quantity used for caps and defaulted quantity edits.";
const HELP_TH_BASIS =
  "Whether stock came from Megaventory physical shelf telemetry when present versus forecast-style on-hand when physical is unavailable.";
const HELP_TH_UNIT_COST = "Megaventory DocumentRowUnitPriceWithoutTaxOrDiscount sourced from product unit cost.";
const HELP_TH_UOM = "Megaventory ProductUnitOfMeasurement defining how quantity and conversion apply.";
const HELP_TH_QTY_TO_TRANSMUTE = "Removal quantity submitted on the minus document for that row in source product units.";
const HELP_TH_CONV =
  "Optional multiplier so target quantity = source quantity × this number. When the field is empty, the server uses the same inference as the placeholder (from source and target unit-of-measure labels). Type a value only when you need to override that inference.";

/** Readable multiplier F shown as ghost text when inferred (target qty = source qty × F). */
function formatInferredMultiplierForPlaceholder(value: number): string {
  if (!Number.isFinite(value)) {
    return "";
  }
  const compact = Number(value.toPrecision(12));
  const nearestInteger = Math.round(compact);
  if (Math.abs(compact - nearestInteger) < 1e-9) {
    return String(nearestInteger);
  }
  return String(compact);
}

function FieldLabelWithHelp({
  htmlFor,
  label,
  hoverHint,
}: {
  htmlFor?: string;
  label: string;
  hoverHint: string;
}) {
  return (
    <div className="mb-1 flex flex-wrap items-center gap-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      <HoverHintWithGlyph hoverHint={hoverHint} />
    </div>
  );
}

function TableHeaderWithHelp({ label, hoverHint }: { label: string; hoverHint: string }) {
  return (
    <th scope="col" className="border-b border-slate-200 px-2 py-2 text-left align-bottom text-xs font-medium text-slate-600">
      <div className="inline-flex max-w-[13rem] flex-wrap items-center gap-1 leading-tight">
        <span>{label}</span>
        <HoverHintWithGlyph hoverHint={hoverHint} />
      </div>
    </th>
  );
}

type EquivalentSkusResponse = {
  approved_sku: string;
  equivalent_skus: string[];
  megaventory_product_id_by_sku?: Record<string, number>;
};

type InventoryLineRow = {
  sku: string;
  mv_product_id: number | null;
  inventory_location_id: number;
  hub_location_public_id: number | null;
  location_display_name: string | null;
  stock_quantity: number;
  stock_quantity_basis: "physical" | "forecast_on_hand";
  unit_cost: number;
  unit_of_measurement: string | null;
};

type InventoryPayload = {
  rows: InventoryLineRow[];
  location_megaventory_ids: number[];
};

type TransmuteExecuteSuccessResponse = {
  ok: boolean;
  document_pairs: number;
  megaventory_pairs?: MegaventoryTransmuteExecutedPair[];
};

type RowDraft = {
  quantity: string;
  manual_factor: string;
};

function stableRowKey(row: InventoryLineRow): string {
  return `${row.sku.toLowerCase()}|${String(row.inventory_location_id)}`;
}

/** True when this snapshot line has on-hand quantity Megaventory can withdraw for transmute. */
function rowHasWithdrawableStock(row: InventoryLineRow): boolean {
  const quantity = row.stock_quantity;
  return typeof quantity === "number" && Number.isFinite(quantity) && quantity > 0;
}

type TransmuteModeTab = "aml-guided" | "manual";

export function TransmutePage() {
  const [transmuteTab, setTransmuteTab] = useState<TransmuteModeTab>("aml-guided");
  const manualMode = transmuteTab === "manual";
  const [targetSku, setTargetSku] = useState("");
  /** Hub `public.location.id` — Megaventory-linked site where AML transmute reads stock and posts adjustments. */
  const [amlLocationId, setAmlLocationId] = useState<number | "">("");

  const [manualFromSku, setManualFromSku] = useState("");
  const [manualToSku, setManualToSku] = useState("");
  const [manualLocationId, setManualLocationId] = useState<number | "">("");
  const [manualQuantity, setManualQuantity] = useState("");
  const [manualFactor, setManualFactor] = useState("");

  const [remark, setRemark] = useState("");
  const [inventoryRows, setInventoryRows] = useState<InventoryLineRow[]>([]);
  const [rowDraft, setRowDraft] = useState<Record<string, RowDraft>>({});
  /** Single alternate row key chosen as the withdrawal source (not the target customer part number row). */
  const [selectedSourceRowKey, setSelectedSourceRowKey] = useState<string | null>(null);

  const [lastError, setLastError] = useState<string | null>(null);
  const [sessionLog, setSessionLog] = useState<
    Array<{
      iso: string;
      manual: boolean;
      summary: string;
      ok: boolean;
      megaventory_pairs?: MegaventoryTransmuteExecutedPair[];
    }>
  >([]);

  const operationalLocations = useTransferEndpointLocations();

  const equivalentQuery = useQuery({
    queryKey: ["mrp", "transmute", "equiv", targetSku.trim()],
    enabled: !manualMode && targetSku.trim().length > 0,
    queryFn: async () =>
      apiFetch<EquivalentSkusResponse>("/api/mrp/transmute/equivalent-skus", {
        query: { cpn: targetSku.trim() },
      }),
  });

  /**
   * Fingerprint AML equivalent SKUs for the current target once the equivalents query succeeds.
   * Used only to gate auto reload: avoids rerunning inventory on unrelated query metadata updates
   * (for example refetch timestamps) while still reloading when the alternates list actually changes.
   */
  const amlEquivInventoryKey = useMemo(() => {
    if (!equivalentQuery.isSuccess || equivalentQuery.data == null) {
      return null;
    }
    const trimmedTarget = targetSku.trim();
    if (trimmedTarget === "") {
      return null;
    }
    const data = equivalentQuery.data;
    if (data.approved_sku.trim().toLowerCase() !== trimmedTarget.toLowerCase()) {
      return null;
    }
    const sorted = [...data.equivalent_skus]
      .map((sku) => sku.trim())
      .filter((sku) => sku.length > 0)
      .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: "base" }));
    return `${data.approved_sku.trim()}\0${sorted.join("\0")}`;
  }, [equivalentQuery.isSuccess, equivalentQuery.data, targetSku]);

  const loadInventoryMutation = useMutation({
    mutationFn: async () => {
      if (manualMode) {
        throw new Error("Manual mode skips the AML inventory loader; submit the manual form.");
      }
      const trimmedTarget = targetSku.trim();
      if (trimmedTarget === "") throw new Error("Choose a target SKU first.");
      const locationPublic =
        typeof amlLocationId === "number" ? amlLocationId : Number(amlLocationId);
      if (!Number.isInteger(locationPublic) || locationPublic <= 0) {
        throw new Error("Pick a hub location.");
      }
      const locIdsSelected = [locationPublic];
      const equiv = equivalentQuery.data?.equivalent_skus ?? [];
      const skuSet = new Set<string>(equiv);
      skuSet.add(trimmedTarget);
      const skus = [...skuSet];
      if (skus.length === 0 || locIdsSelected.length === 0) {
        throw new Error("Pick at least one location with a Megaventory warehouse link.");
      }
      const payload = await apiFetch<InventoryPayload>("/api/mrp/transmute/inventory-lines", {
        method: "POST",
        body: {
          skus,
          location_ids: locIdsSelected,
        },
      });
      const nextDraft: Record<string, RowDraft> = {};
      let firstAlternateKey: string | null = null;
      for (const row of payload.rows) {
        const key = stableRowKey(row);
        const isTargetRow = row.sku.trim().toUpperCase() === trimmedTarget.toUpperCase();
        if (!isTargetRow && rowHasWithdrawableStock(row) && firstAlternateKey === null) {
          firstAlternateKey = key;
        }
        nextDraft[key] = {
          quantity:
            typeof row.stock_quantity === "number" && Number.isFinite(row.stock_quantity)
              ? String(row.stock_quantity)
              : "",
          manual_factor: "",
        };
      }
      setInventoryRows(payload.rows);
      setRowDraft(nextDraft);
      setSelectedSourceRowKey(firstAlternateKey);
      return payload;
    },
    onSuccess: () => setLastError(null),
    onError: (error) => setLastError(error instanceof Error ? error.message : String(error)),
  });

  const wiredLocations = operationalLocations.locations;

  const amlSelectedSourceRow = useMemo(() => {
    if (selectedSourceRowKey === null) {
      return null;
    }
    return inventoryRows.find((row) => stableRowKey(row) === selectedSourceRowKey) ?? null;
  }, [inventoryRows, selectedSourceRowKey]);

  const amlExecuteEnabled = useMemo(() => {
    const trimmedTarget = targetSku.trim();
    if (trimmedTarget === "") {
      return false;
    }
    if (amlSelectedSourceRow == null) {
      return false;
    }
    if (amlSelectedSourceRow.sku.trim().toUpperCase() === trimmedTarget.toUpperCase()) {
      return false;
    }
    return rowHasWithdrawableStock(amlSelectedSourceRow);
  }, [amlSelectedSourceRow, targetSku]);

  const targetInventorySnapshotRow = useMemo(() => {
    const trimmed = targetSku.trim();
    if (trimmed === "") {
      return null;
    }
    const upper = trimmed.toUpperCase();
    return inventoryRows.find((row) => row.sku.trim().toUpperCase() === upper) ?? null;
  }, [inventoryRows, targetSku]);

  const inferredSourceToTargetMultiplier = useMemo(() => {
    if (amlSelectedSourceRow == null || targetInventorySnapshotRow == null) {
      return null;
    }
    return conversionFactorTargetPerSource(
      amlSelectedSourceRow.unit_of_measurement ?? undefined,
      targetInventorySnapshotRow.unit_of_measurement ?? undefined,
    );
  }, [amlSelectedSourceRow, targetInventorySnapshotRow]);

  const manualFactorDraftForSelection =
    selectedSourceRowKey === null ? "" : (rowDraft[selectedSourceRowKey]?.manual_factor ?? "");

  const amlMultiplierPlaceholderGhost = useMemo(() => {
    const trimmedDraft = manualFactorDraftForSelection.trim();
    if (trimmedDraft !== "") {
      return "";
    }
    if (amlSelectedSourceRow == null) {
      return "";
    }
    if (!rowHasWithdrawableStock(amlSelectedSourceRow)) {
      return "";
    }
    if (amlSelectedSourceRow.sku.trim().toUpperCase() === targetSku.trim().toUpperCase()) {
      return "";
    }
    if (inferredSourceToTargetMultiplier != null) {
      return formatInferredMultiplierForPlaceholder(inferredSourceToTargetMultiplier);
    }
    return "Not derived from unit labels — enter a number";
  }, [
    manualFactorDraftForSelection,
    amlSelectedSourceRow,
    targetSku,
    inferredSourceToTargetMultiplier,
  ]);

  const AUTO_LOAD_SNAPSHOT_MS = 450;

  const loadInventoryMutateAsyncRef = useRef(loadInventoryMutation.mutateAsync);
  loadInventoryMutateAsyncRef.current = loadInventoryMutation.mutateAsync;

  useEffect(() => {
    if (transmuteTab !== "aml-guided") {
      return;
    }
    const trimmedTarget = targetSku.trim();
    if (trimmedTarget === "") {
      return;
    }
    const locationPublic = typeof amlLocationId === "number" ? amlLocationId : Number(amlLocationId);
    if (!Number.isInteger(locationPublic) || locationPublic <= 0) {
      return;
    }
    if (wiredLocations.length === 0) {
      return;
    }
    if (amlEquivInventoryKey === null) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      void loadInventoryMutateAsyncRef.current().catch(() => undefined);
    }, AUTO_LOAD_SNAPSHOT_MS);

    return () => window.clearTimeout(timeoutId);
  }, [transmuteTab, targetSku, amlLocationId, wiredLocations.length, amlEquivInventoryKey]);

  const executeMutation = useMutation({
    mutationFn: async () => {
      setLastError(null);
      const trimmedRemark = remark.trim();

      if (manualMode) {
        const qty = Number(manualQuantity);
        const locIdRaw = typeof manualLocationId === "number" ? manualLocationId : Number(manualLocationId);
        if (!manualFromSku.trim() || !manualToSku.trim()) throw new Error("Fill in From SKU and To SKU.");
        if (!Number.isInteger(locIdRaw) || locIdRaw <= 0) throw new Error("Pick a hub location.");
        if (!Number.isFinite(qty) || qty <= 0) throw new Error("Manual quantity must be a positive number.");
        const mf = manualFactor.trim();
        let manual_override: number | null | undefined;
        if (mf === "") manual_override = undefined;
        else {
          const parsed = Number(mf);
          if (!Number.isFinite(parsed) || parsed <= 0) throw new Error("Manual conversion factor must be positive.");
          manual_override = parsed;
        }
        return apiFetch<TransmuteExecuteSuccessResponse>("/api/mrp/transmute/execute", {
          method: "POST",
          body: {
            manual: true,
            target_sku: manualToSku.trim(),
            aml_context_sku: manualToSku.trim(),
            remark: trimmedRemark !== "" ? trimmedRemark : undefined,
            lines: [
              {
                from_sku: manualFromSku.trim(),
                location_public_id: locIdRaw,
                quantity: qty,
                manual_target_units_per_source_unit:
                  manual_override === undefined ? null : manual_override,
              },
            ],
          },
        });
      }

      const trimmedTarget = targetSku.trim();
      if (trimmedTarget === "") throw new Error("Choose a target SKU.");

      if (selectedSourceRowKey === null) {
        throw new Error("Pick a source stock keeping unit row (an alternate, not the target).");
      }

      const sourceRow = inventoryRows.find((row) => stableRowKey(row) === selectedSourceRowKey);
      if (sourceRow == null) {
        throw new Error("Source row is no longer in the table; reload inventory.");
      }
      if (sourceRow.sku.trim().toUpperCase() === trimmedTarget.toUpperCase()) {
        throw new Error("Choose an alternate source row, not the target customer part number.");
      }
      if (!rowHasWithdrawableStock(sourceRow)) {
        throw new Error("Pick a source row that has on-hand stock to withdraw.");
      }
      const draft = rowDraft[selectedSourceRowKey];
      if (draft == null) {
        throw new Error("Missing row state; reload inventory.");
      }
      if (sourceRow.hub_location_public_id == null || !Number.isInteger(sourceRow.hub_location_public_id)) {
        throw new Error(`Row ${sourceRow.sku} is missing hub location mapping; reload inventory.`);
      }
      const quantity = Number(draft.quantity);
      if (!Number.isFinite(quantity) || quantity <= 0) {
        throw new Error("Enter a positive quantity to transmute for the selected source row.");
      }
      const factorRaw = draft.manual_factor.trim();
      let manual_target_units_per_source_unit: number | null = null;
      if (factorRaw !== "") {
        const parsedFactor = Number(factorRaw);
        if (!Number.isFinite(parsedFactor) || parsedFactor <= 0) {
          throw new Error("Conversion override for the source row must be a positive number.");
        }
        manual_target_units_per_source_unit = parsedFactor;
      }

      const lines = [
        {
          from_sku: sourceRow.sku.trim(),
          location_public_id: sourceRow.hub_location_public_id,
          quantity,
          manual_target_units_per_source_unit:
            manual_target_units_per_source_unit === null ? null : manual_target_units_per_source_unit,
        },
      ];

      return apiFetch<TransmuteExecuteSuccessResponse>("/api/mrp/transmute/execute", {
        method: "POST",
        body: {
          manual: false,
          target_sku: trimmedTarget,
          aml_context_sku: trimmedTarget,
          remark: trimmedRemark !== "" ? trimmedRemark : undefined,
          lines,
        },
      });
    },
    onSuccess: (data) => {
      const summary = `Created ${String(data.document_pairs)} adjustment pair(s).`;
      setSessionLog((prev) => [
        {
          iso: new Date().toISOString(),
          manual: manualMode,
          summary,
          ok: true,
          megaventory_pairs: data.megaventory_pairs,
        },
        ...prev,
      ]);
      if (!manualMode) {
        void loadInventoryMutation.mutateAsync().catch(() => undefined);
      }
    },
    onError: (error) => {
      let message =
        error instanceof ApiError ? error.message : error instanceof Error ? error.message : String(error);
      const supplemental: string[] = [];
      if (error instanceof ApiError && error.body && typeof error.body === "object") {
        const body = error.body as Record<string, unknown>;
        const failed = body.transmute_totals_verification_failed;
        if (failed && typeof failed === "object") {
          const mismatch = failed as Record<string, unknown>;
          supplemental.push(
            `Withdrawal document id ${String(mismatch.minus_document_id)} line total ${String(mismatch.minus_line_total_money)}; receipt document id ${String(mismatch.plus_document_id)} line total ${String(mismatch.plus_line_total_money)}.`,
          );
        }
        const attemptsUnknown = body.megaventory_cancel_attempts;
        if (Array.isArray(attemptsUnknown)) {
          supplemental.push(
            ...attemptsUnknown
              .map((entry): string | null => {
                if (!entry || typeof entry !== "object") return null;
                const attempt = entry as Record<string, unknown>;
                const idRaw = attempt.document_id;
                const idLabel = typeof idRaw === "number" || typeof idRaw === "string" ? String(idRaw) : "?";
                const detail = typeof attempt.detail === "string" ? attempt.detail.trim() : "";
                const ok = attempt.ok === true;
                const detailPhrase = detail !== "" ? ` (${detail})` : "";
                return ok
                  ? `Megaventory document ${idLabel} cancellation succeeded.${detailPhrase}`
                  : `Megaventory document ${idLabel} cancellation failed.${detailPhrase}`;
              })
              .filter((text): text is string => text != null && text.trim() !== ""),
          );
        }
      }
      if (supplemental.length > 0) {
        message = [message, ...supplemental].join("\n");
      }
      setLastError(message);
      setSessionLog((prev) => [
        {
          iso: new Date().toISOString(),
          manual: manualMode,
          summary: message,
          ok: false,
        },
        ...prev,
      ]);
    },
  });

  return (
    <div className="space-y-6">
      <PageTitleWithHelp hoverHint={HELP_TRANSMUTE_PAGE}>Transmute</PageTitleWithHelp>

      {lastError != null && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800 whitespace-pre-wrap">          {lastError}
        </div>
      )}

      <div className="flex flex-wrap items-end gap-1 border-b border-slate-200 pb-px">
        <div className="flex min-w-0 flex-1 flex-wrap gap-1" role="tablist" aria-label="Transmute mode">
          <div
            className={cn(
              "inline-flex items-stretch rounded-t-md transition-colors",
              transmuteTab === "aml-guided"
                ? "relative z-10 border border-b-0 border-slate-200 bg-white text-slate-900 shadow-sm"
                : "border border-transparent bg-slate-50 text-slate-600 hover:bg-slate-100",
            )}
          >
            <button
              type="button"
              role="tab"
              id="transmute-tab-aml-guided"
              aria-selected={transmuteTab === "aml-guided"}
              aria-controls="transmute-panel-aml-guided"
              className={cn(
                "px-3 py-2 text-sm font-medium transition-colors",
                transmuteTab === "aml-guided" ? "text-slate-900" : "text-slate-600",
              )}
              onClick={() => setTransmuteTab("aml-guided")}
            >
              AML-guided
            </button>
            <span className="flex items-center pr-2">
              <HoverHintWithGlyph hoverHint={HELP_AML_CARD} />
            </span>
          </div>
          <div
            className={cn(
              "inline-flex items-stretch rounded-t-md transition-colors",
              transmuteTab === "manual"
                ? "relative z-10 border border-b-0 border-slate-200 bg-white text-slate-900 shadow-sm"
                : "border border-transparent bg-slate-50 text-slate-600 hover:bg-slate-100",
            )}
          >
            <button
              type="button"
              role="tab"
              id="transmute-tab-manual"
              aria-selected={transmuteTab === "manual"}
              aria-controls="transmute-panel-manual"
              className={cn(
                "px-3 py-2 text-sm font-medium transition-colors",
                transmuteTab === "manual" ? "text-slate-900" : "text-slate-600",
              )}
              onClick={() => setTransmuteTab("manual")}
            >
              Manual
            </button>
            <span className="flex items-center pr-2">
              <HoverHintWithGlyph hoverHint={HELP_MANUAL_CARD} />
            </span>
          </div>
        </div>
      </div>

      {transmuteTab === "aml-guided" ? (
        <div
          role="tabpanel"
          id="transmute-panel-aml-guided"
          aria-labelledby="transmute-tab-aml-guided"
          className="space-y-0"
        >
        <Card>
          <CardBody className="space-y-4">
            <div className="grid gap-4 lg:grid-cols-2">
              <div>
                <FieldLabelWithHelp htmlFor="transmute-target" label="Target customer part number" hoverHint={HELP_TARGET_SKU} />
                <AssemblySkuCombobox
                  id="transmute-target"
                  enforceCatalogSku
                  value={targetSku}
                  onChange={setTargetSku}
                  onBlurCommittedWithSku={(committed) => setTargetSku(committed)}
                  placeholder="Search stocked customer part number…"
                  className="w-full"
                />
              </div>
              <div>
                <FieldLabelWithHelp htmlFor="transmute-aml-location" label="Location" hoverHint={HELP_AML_LOCATION} />
                <select
                  id="transmute-aml-location"
                  className={cn(
                    "h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm",
                    wiredLocations.length === 0 || operationalLocations.isLoading ? "opacity-60" : "",
                  )}
                  value={amlLocationId === "" ? "" : String(amlLocationId)}
                  disabled={wiredLocations.length === 0 || operationalLocations.isLoading}
                  onChange={(event) => {
                    const value = event.target.value;
                    setAmlLocationId(value === "" ? "" : Number(value));
                  }}
                >
                  <option value="">Pick where you are transmuting</option>
                  <OperationalLocationNativeSelectOptions locations={wiredLocations} />
                </select>
              </div>
            </div>

            {wiredLocations.length === 0 && !operationalLocations.isLoading ? (
              <p className="text-sm text-amber-700">No active warehouses are available for adjustments.</p>
            ) : null}

            <div>
              <FieldLabelWithHelp
                htmlFor="transmute-remark-aml"
                label="Optional remark"
                hoverHint={HELP_OPTIONAL_REMARK}
              />
              <Input
                id="transmute-remark-aml"
                value={remark}
                onChange={(event) => setRemark(event.target.value)}
                placeholder="Leave blank for default comment"
              />
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse text-left text-sm">
                <thead>
                  <tr>
                    <TableHeaderWithHelp label="Source" hoverHint={HELP_TH_SOURCE_ROW} />
                    <TableHeaderWithHelp label="Stock keeping unit" hoverHint={HELP_TH_SKU} />
                    <TableHeaderWithHelp label="Location" hoverHint={HELP_TH_LOCATION} />
                    <TableHeaderWithHelp label="Stock" hoverHint={HELP_TH_STOCK} />
                    <TableHeaderWithHelp label="Basis" hoverHint={HELP_TH_BASIS} />
                    <TableHeaderWithHelp label="Unit cost" hoverHint={HELP_TH_UNIT_COST} />
                    <TableHeaderWithHelp label="Unit of measure" hoverHint={HELP_TH_UOM} />
                  </tr>
                </thead>
                <tbody>
                  {inventoryRows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-2 py-4 text-slate-500">
                        Load inventory to see rows.
                      </td>
                    </tr>
                  ) : (
                    inventoryRows.map((row) => {
                      const key = stableRowKey(row);
                      const isTarget = row.sku.trim().toUpperCase() === targetSku.trim().toUpperCase();
                      const isSource = selectedSourceRowKey === key;
                      const canWithdrawSource = rowHasWithdrawableStock(row);
                      const sourceRadioEnabled = !isTarget && canWithdrawSource;
                      return (
                        <tr
                          key={key}
                          className={cn(
                            "border-b border-slate-100",
                            isTarget && "bg-slate-50",
                            !isTarget && isSource && sourceRadioEnabled && "bg-sky-50/70",
                          )}
                        >
                          <td className="px-2 py-2">
                            {isTarget ? (
                              <span className="inline-block rounded border border-slate-200 bg-white px-2 py-0.5 text-xs font-medium text-slate-600">
                                Target (receiving only)
                              </span>
                            ) : !canWithdrawSource ? (
                              <span
                                className="inline-block rounded border border-slate-200 bg-white px-2 py-0.5 text-xs font-medium text-slate-600"
                                title="This line has no on-hand stock to withdraw at this warehouse."
                                aria-label={`${row.sku}: no on-hand stock to withdraw at this warehouse.`}
                              >
                                No stock
                              </span>
                            ) : (
                              <input
                                type="radio"
                                name="transmute-aml-source-row"
                                className="h-4 w-4 accent-sky-600"
                                checked={isSource}
                                aria-label={`Withdraw from source part number ${row.sku}`}
                                onChange={() => {
                                  setSelectedSourceRowKey(key);
                                }}
                              />
                            )}
                          </td>
                          <td className="px-2 py-2 font-mono text-xs">
                            <MrpSkuDetailLink sku={row.sku} tab="inventory" className="text-sky-700 underline">
                              {row.sku}
                            </MrpSkuDetailLink>
                          </td>
                          <td className="px-2 py-2">{row.location_display_name ?? "—"}</td>
                          <td className="px-2 py-2">{row.stock_quantity}</td>
                          <td className="px-2 py-2">{row.stock_quantity_basis.replace("_", " ")}</td>
                          <td className="px-2 py-2">{row.unit_cost.toFixed(4)}</td>
                          <td className="px-2 py-2">{row.unit_of_measurement ?? "—"}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="rounded-md border border-slate-200 bg-slate-50/80 p-4">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Selected source withdrawal
              </p>
              {selectedSourceRowKey === null ? (
                <p className="text-sm text-slate-600">Pick a source row in the table to set quantity and conversion.</p>
              ) : amlSelectedSourceRow == null ? (
                <p className="text-sm text-slate-600">Source row is no longer in the table; reload the inventory snapshot.</p>
              ) : amlSelectedSourceRow.sku.trim().toUpperCase() === targetSku.trim().toUpperCase() ? (
                <p className="text-sm text-slate-600">The target customer part number row cannot be a withdrawal source.</p>
              ) : !rowHasWithdrawableStock(amlSelectedSourceRow) ? (
                <p className="text-sm text-slate-600">
                  Part number{" "}
                  <span className="font-mono text-xs">{amlSelectedSourceRow.sku}</span> has no on-hand stock; pick a
                  different source or reload after stock changes.
                </p>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <FieldLabelWithHelp
                      htmlFor="transmute-selected-qty"
                      label={`Quantity to transmute (${amlSelectedSourceRow.sku})`}
                      hoverHint={HELP_TH_QTY_TO_TRANSMUTE}
                    />
                    <Input
                      id="transmute-selected-qty"
                      className="h-10"
                      value={(rowDraft[selectedSourceRowKey] ?? { quantity: "", manual_factor: "" }).quantity}
                      onChange={(event) =>
                        setRowDraft((previous) => {
                          const draft = previous[selectedSourceRowKey] ?? { quantity: "", manual_factor: "" };
                          return {
                            ...previous,
                            [selectedSourceRowKey]: { ...draft, quantity: event.target.value },
                          };
                        })
                      }
                    />
                  </div>
                  <div>
                    <FieldLabelWithHelp
                      htmlFor="transmute-selected-conv"
                      label="Multiplier (optional)"
                      hoverHint={HELP_TH_CONV}
                    />
                    <Input
                      id="transmute-selected-conv"
                      className="h-10"
                      placeholder={amlMultiplierPlaceholderGhost}
                      value={(rowDraft[selectedSourceRowKey] ?? { quantity: "", manual_factor: "" }).manual_factor}
                      onChange={(event) =>
                        setRowDraft((previous) => {
                          const draft = previous[selectedSourceRowKey] ?? { quantity: "", manual_factor: "" };
                          return {
                            ...previous,
                            [selectedSourceRowKey]: { ...draft, manual_factor: event.target.value },
                          };
                        })
                      }
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={() => loadInventoryMutation.mutate()}
                disabled={
                  manualMode ||
                  loadInventoryMutation.isPending ||
                  amlLocationId === "" ||
                  wiredLocations.length === 0 ||
                  targetSku.trim() === ""
                }
              >
                {loadInventoryMutation.isPending ? "Loading inventory…" : "Reload inventory snapshot"}
              </Button>
              <Button
                type="button"
                onClick={() => executeMutation.mutate()}
                disabled={executeMutation.isPending || manualMode || !amlExecuteEnabled}
              >
                {executeMutation.isPending ? "Transmuting…" : "Transmute into target"}
              </Button>
            </div>
          </CardBody>
        </Card>
        </div>
      ) : (
        <div
          role="tabpanel"
          id="transmute-panel-manual"
          aria-labelledby="transmute-tab-manual"
          className="space-y-0"
        >
        <Card>
          <CardBody className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <FieldLabelWithHelp htmlFor="manual-from" label="From stock keeping unit" hoverHint={HELP_MANUAL_FROM} />
                <AssemblySkuCombobox
                  id="manual-from"
                  enforceCatalogSku
                  value={manualFromSku}
                  onChange={setManualFromSku}
                  onBlurCommittedWithSku={(committed) => setManualFromSku(committed)}
                  placeholder="Search source stock keeping unit…"
                  className="w-full"
                />
              </div>
              <div>
                <FieldLabelWithHelp htmlFor="manual-to" label="To stock keeping unit" hoverHint={HELP_MANUAL_TO} />
                <AssemblySkuCombobox
                  id="manual-to"
                  enforceCatalogSku
                  value={manualToSku}
                  onChange={setManualToSku}
                  onBlurCommittedWithSku={(committed) => setManualToSku(committed)}
                  placeholder="Search receiving stock keeping unit…"
                  className="w-full"
                />
              </div>
              <div>
                <FieldLabelWithHelp htmlFor="manual-loc" label="Location" hoverHint={HELP_MANUAL_LOCATION} />
                <select
                  id="manual-loc"
                  className={cn(
                    "h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm",
                    wiredLocations.length === 0 ? "opacity-60" : "",
                  )}
                  value={manualLocationId === "" ? "" : String(manualLocationId)}
                  disabled={wiredLocations.length === 0}
                  onChange={(event) => {
                    const value = event.target.value;
                    setManualLocationId(value === "" ? "" : Number(value));
                  }}
                >
                  <option value="">Pick a location</option>
                  <OperationalLocationNativeSelectOptions locations={wiredLocations} />
                </select>
              </div>
              <div>
                <FieldLabelWithHelp htmlFor="manual-qty" label="Quantity (source units)" hoverHint={HELP_MANUAL_QTY} />
                <Input
                  id="manual-qty"
                  value={manualQuantity}
                  onChange={(event) => setManualQuantity(event.target.value)}
                  placeholder=""
                />
              </div>
              <div>
                <FieldLabelWithHelp
                  htmlFor="manual-factor"
                  label="Multiplier (optional)"
                  hoverHint={HELP_MANUAL_FACTOR}
                />
                <Input
                  id="manual-factor"
                  value={manualFactor}
                  onChange={(event) => setManualFactor(event.target.value)}
                  placeholder="Example: pound to gram via 453.59237 target units per one pound"
                />
              </div>
            </div>
            <div>
              <FieldLabelWithHelp
                htmlFor="transmute-remark-manual"
                label="Optional remark"
                hoverHint={HELP_OPTIONAL_REMARK}
              />
              <Input
                id="transmute-remark-manual"
                value={remark}
                onChange={(event) => setRemark(event.target.value)}
              />
            </div>
            <Button type="button" onClick={() => executeMutation.mutate()} disabled={executeMutation.isPending}>
              {executeMutation.isPending ? "Transmuting…" : "Run manual transmute"}
            </Button>
          </CardBody>
        </Card>
        </div>
      )}

      {sessionLog.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitleWithHelp hoverHint={HELP_SESSION_CARD}>This session</CardTitleWithHelp>
          </CardHeader>
          <CardBody className="space-y-3 text-sm">
            {sessionLog.slice(0, 12).map((entry, index) => (
              <div
                key={`${entry.iso}-${String(index)}`}
                className={cn("rounded-md border px-3 py-2", entry.ok ? "border-emerald-200 bg-emerald-50" : "border-rose-100 bg-white")}
              >
                <span className="mr-3 text-xs text-slate-500">{entry.iso}</span>
                <span className="font-semibold">{entry.manual ? "Manual " : ""}</span>
                <span className={entry.ok ? "text-emerald-800" : "text-rose-700"}>{entry.summary}</span>
                {entry.ok && entry.megaventory_pairs != null && entry.megaventory_pairs.length > 0 ? (
                  <div className="mt-2 border-t border-emerald-200/80 pt-2 text-emerald-950">
                    <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-600">
                      Megaventory documents (opens in new tab)
                    </p>
                    <ul className="space-y-2 text-sm">
                      {entry.megaventory_pairs.map((pair, pairIndex) => {
                        const wd = pair.withdrawal_adjustment;
                        const rc = pair.receipt_adjustment;
                        const withdrawalLabel =
                          wd.document_no !== ""
                            ? `${wd.document_type_abbreviation} ${wd.document_no}`
                            : `${wd.document_type_abbreviation} (#${String(wd.mv_document_id)})`;
                        const receiptLabel =
                          rc.document_no !== ""
                            ? `${rc.document_type_abbreviation} ${rc.document_no}`
                            : `${rc.document_type_abbreviation} (#${String(rc.mv_document_id)})`;
                        return (
                          <li
                            key={`${String(wd.mv_document_id)}-${String(rc.mv_document_id)}-${String(pairIndex)}`}
                          >
                            <span className="font-medium text-slate-700">{`Pair ${String(pairIndex + 1)}: `}</span>
                            <span className="text-slate-600">Withdraw stock </span>
                            <a
                              href={wd.megaventory_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sky-700 underline underline-offset-2 hover:text-sky-900"
                            >
                              {withdrawalLabel}
                            </a>
                            <span className="text-slate-600"> · Receive stock </span>
                            <a
                              href={rc.megaventory_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sky-700 underline underline-offset-2 hover:text-sky-900"
                            >
                              {receiptLabel}
                            </a>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ) : null}
              </div>
            ))}
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}
